<script setup lang="ts">
import { type LastCrawlingDate, type CrawlingStatusParams, crawlingStatusApi } from '@/api/entities/review/crawlingStatus'
import { clientsApi, type Client } from '@/api/entities'

definePage({
  meta: {
    title: '크롤링 현황',
    action: 'read',
    subject: 'Upload',
  },
})

const { snackbar, handleError } = useErrorHandler()

// 상태
const page = ref(1)
const itemsPerPage = ref(15)
const totalItems = ref(0)
const loading = ref(false)
const items = ref<LastCrawlingDate[]>([])

// 필터
const clients = ref<Client[]>([])
const selectedClientId = ref<number | null>(null)
const selectedMonthlyState = ref<'Y' | 'N' | null>(null)
const dateFrom = ref<string | null>(null)
const dateTo = ref<string | null>(null)

const monthlyStateOptions = [
  { title: '전체', value: null },
  { title: 'Y (활성)', value: 'Y' },
  { title: 'N (비활성)', value: 'N' },
]

// 행별 액션 로딩 상태
const actionLoading = ref<Record<number, string>>({})

// 테이블 헤더
const headers = [
  { title: 'ID', key: 'id', sortable: false },
  { title: '고객사', key: 'client_name', sortable: false },
  { title: '드라이버명', key: 'driver_name', sortable: false },
  { title: '마지막 크롤링일', key: 'last_crawling_date', sortable: false },
  { title: '업로드 상태', key: 'upload_state', sortable: false },
  { title: '확인 여부', key: 'is_checked', sortable: false },
  { title: '액션', key: 'actions', sortable: false },
]

// 데이터 로드
const loadItems = async () => {
  loading.value = true
  try {
    const params: CrawlingStatusParams = {
      page: page.value,
      per_page: itemsPerPage.value,
      client_id: selectedClientId.value || undefined,
      monthly_state: selectedMonthlyState.value || undefined,
      date_from: dateFrom.value || undefined,
      date_to: dateTo.value || undefined,
    }

    const response = await crawlingStatusApi.listCrawlingStatus(params)
    if (response.success && response.data) {
      const data = response.data as any

      items.value = data.data || data
      totalItems.value = data.meta?.total || data.total || items.value.length
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    loading.value = false
  }
}

// 고객사 목록 로드
const loadClients = async () => {
  try {
    const response = await clientsApi.listClients({ per_page: 500 } as any)
    if (response.success && response.data) {
      const data = response.data as any

      clients.value = data.data || data
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 감시자
watch(page, () => {
  loadItems()
})

watch([itemsPerPage, selectedClientId, selectedMonthlyState, dateFrom, dateTo], () => {
  if (page.value === 1)
    loadItems()
  else
    page.value = 1
})

// 초기화
onMounted(() => {
  loadItems()
  loadClients()
})

// 마지막 크롤링일 갱신
const handleFetch = async (item: LastCrawlingDate) => {
  if (!item.id)
    return

  actionLoading.value = { ...actionLoading.value, [item.id]: 'fetch' }
  try {
    const response = await crawlingStatusApi.fetchCrawlingDate(item.id)
    if (response.success && response.data) {
      // 해당 행 데이터 갱신
      const idx = items.value.findIndex(i => i.id === item.id)
      if (idx !== -1)
        items.value[idx] = response.data as LastCrawlingDate

      snackbar.success('마지막 크롤링일이 갱신되었습니다.')
    }
    else {
      snackbar.error('갱신에 실패했습니다.')
    }
  }
  catch (error: any) {
    const status = error?.response?.status || error?.status
    const message = error?.response?.data?.message || error?.data?.message

    if (status === 400 && message)
      snackbar.error(message)
    else
      handleError(error, { notificationMessage: '마지막 크롤링일 갱신 중 오류가 발생했습니다.' })
  }
  finally {
    const next = { ...actionLoading.value }

    delete next[item.id]
    actionLoading.value = next
  }
}

// 즉시 재시도
const handleInstantRetry = async (item: LastCrawlingDate) => {
  if (!item.id)
    return

  actionLoading.value = { ...actionLoading.value, [item.id]: 'retry' }
  try {
    const response = await crawlingStatusApi.instantRetry(item.id)
    if (response.success)
      snackbar.success('즉시 재시도가 요청되었습니다.')
    else
      snackbar.error('즉시 재시도에 실패했습니다.')
  }
  catch (error: any) {
    const status = error?.response?.status || error?.status
    const message = error?.response?.data?.message || error?.data?.message

    if (status === 400 && message)
      snackbar.error(message)
    else
      handleError(error, { notificationMessage: '즉시 재시도 중 오류가 발생했습니다.' })
  }
  finally {
    const next = { ...actionLoading.value }

    delete next[item.id]
    actionLoading.value = next
  }
}

// 업로드 상태 배지 색상
const getUploadStateColor = (state: string | null) => {
  if (!state)
    return 'default'
  switch (state.toLowerCase()) {
    case 'success':
    case 'done':
      return 'success'
    case 'fail':
    case 'error':
      return 'error'
    case 'pending':
    case 'waiting':
      return 'warning'
    default:
      return 'secondary'
  }
}

// 날짜 포맷
const formatDate = (dateString: string | null) => {
  if (!dateString)
    return '-'
  const date = new Date(dateString)

  return new Intl.DateTimeFormat('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex align-center justify-space-between flex-wrap gap-4 pa-6">
      <span>크롤링 현황</span>
      <VBtn
        icon="tabler-refresh"
        variant="text"
        :loading="loading"
        @click="loadItems"
      />
    </VCardTitle>

    <VDivider />

    <VCardText>
      <!-- 필터 -->
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <AppSelect
            v-model="selectedClientId"
            :items="clients"
            item-title="name"
            item-value="id"
            placeholder="고객사 선택"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <AppSelect
            v-model="selectedMonthlyState"
            :items="monthlyStateOptions"
            placeholder="Monthly State"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <AppTextField
            v-model="dateFrom"
            type="date"
            label="시작일"
            clearable
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <AppTextField
            v-model="dateTo"
            type="date"
            label="종료일"
            clearable
          />
        </VCol>
      </VRow>

      <VDataTableServer
        v-model:page="page"
        v-model:items-per-page="itemsPerPage"
        :headers="headers"
        :items="items"
        :items-length="totalItems"
        :loading="loading"
        class="text-no-wrap"
      >
        <!-- 고객사 -->
        <template #item.client_name="{ item }">
          {{ item.driver_config?.client?.name || '-' }}
        </template>

        <!-- 드라이버명 -->
        <template #item.driver_name="{ item }">
          {{ item.driver_config?.name || '-' }}
        </template>

        <!-- 마지막 크롤링일 -->
        <template #item.last_crawling_date="{ item }">
          {{ formatDate(item.last_crawling_date) }}
        </template>

        <!-- 업로드 상태 -->
        <template #item.upload_state="{ item }">
          <VChip
            v-if="item.upload_state"
            :color="getUploadStateColor(item.upload_state)"
            size="small"
            variant="tonal"
          >
            {{ item.upload_state }}
          </VChip>
          <span v-else>-</span>
        </template>

        <!-- 확인 여부 -->
        <template #item.is_checked="{ item }">
          <VIcon
            :icon="item.is_checked ? 'tabler-check' : 'tabler-x'"
            :color="item.is_checked ? 'success' : 'error'"
          />
        </template>

        <!-- 액션 버튼 -->
        <template #item.actions="{ item }">
          <div class="d-flex gap-2">
            <VBtn
              size="small"
              color="primary"
              variant="tonal"
              :loading="actionLoading[item.id] === 'fetch'"
              :disabled="!!actionLoading[item.id]"
              @click="handleFetch(item)"
            >
              크롤링일 갱신
            </VBtn>
            <VBtn
              size="small"
              color="warning"
              variant="tonal"
              :loading="actionLoading[item.id] === 'retry'"
              :disabled="!!actionLoading[item.id]"
              @click="handleInstantRetry(item)"
            >
              즉시 재시도
            </VBtn>
          </div>
        </template>
      </VDataTableServer>
    </VCardText>
  </VCard>

  <!-- 스낵바 -->
  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>
